import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from "remotion";

interface Props {
  label?: string;
  subtitle?: string;
}

/**
 * Branded "Coming Soon" 5-second loop for videos still processing on Cloudflare.
 * Uses iAmerica dark navy + gold palette. Renders at 1920x1080 — scales via Player.
 */
export const ComingSoonLoop: React.FC<Props> = ({
  label = "Coming Soon",
  subtitle = "Preparing your video",
}) => {
  const frame = useCurrentFrame();
  const { durationInFrames, width, height } = useVideoConfig();

  // Progress 0 -> 1 across the loop
  const progress = (frame % durationInFrames) / durationInFrames;

  // Logo subtle pulse
  const pulse = 1 + Math.sin((frame / durationInFrames) * Math.PI * 2) * 0.04;

  // Ring geometry
  const ringRadius = 180;
  const ringStroke = 14;
  const circumference = 2 * Math.PI * ringRadius;
  const dashOffset = circumference * (1 - progress);

  // Shimmer position for "Coming Soon" text
  const shimmerX = interpolate(frame % durationInFrames, [0, durationInFrames], [-200, 200]);

  // Progress bar geometry
  const barWidth = 720;
  const barHeight = 64;


  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070e1e",
        fontFamily: "Inter, system-ui, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Animated diagonal grid pattern */}
      <AbsoluteFill
        style={{
          backgroundImage:
            "linear-gradient(45deg, rgba(252,180,36,0.06) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
          backgroundPosition: `${(frame * 0.3) % 50}px ${(frame * 0.3) % 50}px`,
          opacity: 0.7,
        }}
      />

      {/* Soft radial glow behind logo */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(252,180,36,0.18) 0%, rgba(7,14,30,0) 55%)",
        }}
      />

      {/* Center stack */}
      <AbsoluteFill
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {/* Ring + logo */}
        <div
          style={{
            position: "relative",
            width: (ringRadius + ringStroke) * 2,
            height: (ringRadius + ringStroke) * 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${(ringRadius + ringStroke) * 2} ${(ringRadius + ringStroke) * 2}`}
            style={{ position: "absolute", inset: 0 }}
          >
            {/* Track */}
            <circle
              cx={ringRadius + ringStroke}
              cy={ringRadius + ringStroke}
              r={ringRadius}
              stroke="rgba(252,180,36,0.15)"
              strokeWidth={ringStroke}
              fill="none"
            />
            {/* Progress */}
            <circle
              cx={ringRadius + ringStroke}
              cy={ringRadius + ringStroke}
              r={ringRadius}
              stroke="#fcb424"
              strokeWidth={ringStroke}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${ringRadius + ringStroke} ${ringRadius + ringStroke})`}
              style={{ filter: "drop-shadow(0 0 12px rgba(252,180,36,0.6))" }}
            />
          </svg>

          {/* Logo */}
          <img
            src="/iamerica-logo-yellow.svg"
            alt=""
            style={{
              width: "55%",
              height: "55%",
              objectFit: "contain",
              transform: `scale(${pulse})`,
              filter: "drop-shadow(0 6px 24px rgba(252,180,36,0.35))",
            }}
          />
        </div>

        {/* Rounded "Coming Soon" progress bar */}
        <div
          style={{
            marginTop: 70,
            position: "relative",
            width: barWidth,
            height: barHeight,
            borderRadius: barHeight / 2,
            background: "rgba(252,180,36,0.15)",
            border: "1px solid rgba(252,180,36,0.35)",
            overflow: "hidden",
            boxShadow: "0 0 24px rgba(252,180,36,0.25)",
          }}
        >
          {/* Filled progress */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${progress * 100}%`,
              background:
                "linear-gradient(90deg, #b8830f 0%, #fcb424 50%, #ffe28a 100%)",
              borderRadius: barHeight / 2,
              boxShadow: "0 0 18px rgba(252,180,36,0.6)",
            }}
          />
          {/* Label centered on bar */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "#070e1e",
              textShadow: "0 1px 0 rgba(255,255,255,0.25)",
            }}
          >
            {label}
          </div>
        </div>

        {/* Subtitle with animated dots */}
        <div
          style={{
            marginTop: 24,
            fontSize: 28,
            color: "rgba(255,255,255,0.7)",
            letterSpacing: 0.5,
          }}
        >
          {subtitle}
          {".".repeat(1 + Math.floor((frame / 15) % 3))}
        </div>

      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export default ComingSoonLoop;
